import React from 'react';
import {
  Document, Page, View, Text, Image, StyleSheet,
} from '@react-pdf/renderer';
import { registerCairoFont, safePdfText } from './pdfTextUtils';

registerCairoFont();

const NAVY  = '#0F2A5F';
const ACCENT = '#2563EB';
const LGRAY = '#F3F4F6';
const MGRAY = '#D1D5DB';
const DGRAY = '#6B7280';
const WHITE = '#FFFFFF';
const BLACK = '#111827';

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  const hasDecimals = Math.abs(n % 1) > Number.EPSILON;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const QuotationDocument = ({ quotation, company, companySettings, routes = [], options = {} }) => {
  const { language = 'ar', termsAndConditions = { ar: [], en: [] } } = options;
  const isAr = language === 'ar';
  const L = (en, ar) => isAr ? ar : en;
  const infoLine = (label, value) => {
    const safeLabel = safePdfText(label, '');
    const safeValue = safePdfText(value, '-');
    return isAr ? `${safeValue} - ${safeLabel}` : `${safeLabel}: ${safeValue}`;
  };
  const moneyText = (value) => safePdfText(isAr ? `${fmtMoney(value)} ريال` : `${fmtMoney(value)} SAR`);

  // RTL helpers
  const rowDir = isAr ? 'row-reverse' : 'row';
  const textAlign = isAr ? 'right' : 'left';
  const oppAlign = isAr ? 'left' : 'right';

  const S = StyleSheet.create({
    page:        { fontFamily: 'Cairo', fontSize: 9.5, color: BLACK, backgroundColor: WHITE, paddingBottom: 60, textAlign },
    topBar:      { height: 8, backgroundColor: NAVY },
    topAccent:   { height: 3, backgroundColor: ACCENT },

    headerRow:   { flexDirection: rowDir, justifyContent: 'space-between', alignItems: 'center', padding: '10 24 6 24' },
    logoImg:     { width: 56, height: 44, objectFit: 'contain' },
    logoPlaceholder: { width: 56, height: 44, backgroundColor: LGRAY, borderRadius: 4, justifyContent: 'center', alignItems: 'center' },
    companyBlock: { flex: 1, marginHorizontal: 12, textAlign },
    companyName:  { fontSize: 15, color: NAVY, textAlign },
    companyMeta:  { fontSize: 8.5, color: DGRAY, marginTop: 2, textAlign },
    companyMetaLine: { fontSize: 7.8, color: DGRAY, marginTop: 2, textAlign },

    quoteBox:    { borderWidth: 0.8, borderColor: NAVY, paddingVertical: 4, paddingHorizontal: 8, width: 150 },
    quoteTitle:  { fontSize: 11, color: NAVY, textAlign: 'center', marginBottom: 2 },
    quoteMeta:   { fontSize: 7.5, color: BLACK, textAlign: 'center' },
    quoteMetaBold: { fontSize: 8.4, color: NAVY, textAlign: 'center', marginBottom: 1 },
    quoteMetaLine: { fontSize: 7.2, color: DGRAY, textAlign: 'center', marginTop: 1 },

    divider:     { height: 1, backgroundColor: MGRAY, marginHorizontal: 24, marginVertical: 6 },

    partyRow:    { flexDirection: rowDir, marginHorizontal: 24, marginTop: 4 },
    partyBox:    { flex: 1, borderWidth: 0.6, borderColor: MGRAY, borderRadius: 3, overflow: 'hidden' },
    partyBoxL:   { marginRight: isAr ? 0 : 6, marginLeft: isAr ? 6 : 0 },
    partyBoxR:   { marginLeft: isAr ? 0 : 6, marginRight: isAr ? 6 : 0 },
    partyHead:   { backgroundColor: NAVY, paddingVertical: 4, paddingHorizontal: 10 },
    partyHeadTxt:{ fontSize: 9, color: WHITE, textAlign },
    partyBody:   { padding: 8, minHeight: 42 },
    partyName:   { fontSize: 11, color: BLACK, textAlign },
    partySub:    { fontSize: 8.5, color: DGRAY, marginTop: 3, textAlign },

    secTitleRow: { flexDirection: rowDir, alignItems: 'center', marginHorizontal: 24, marginTop: 10, marginBottom: 5 },
    secBar:      { width: 4, height: 14, backgroundColor: ACCENT, marginRight: isAr ? 0 : 6, marginLeft: isAr ? 6 : 0 },
    secTitle:    { fontSize: 11, color: NAVY },

    table:       { marginHorizontal: 24, borderWidth: 0.6, borderColor: MGRAY, borderRadius: 3 },
    tableHead:   { flexDirection: rowDir, backgroundColor: NAVY },
    th:          { color: WHITE, fontSize: 9, padding: '7 8' },
    tr:          { flexDirection: rowDir, borderTopWidth: 0.5, borderTopColor: MGRAY },
    trAlt:       { backgroundColor: '#F9FAFB' },
    td:          { fontSize: 9, color: BLACK, padding: '6 8' },
    colNum:      { width: '8%', textAlign: 'center' },
    colFrom:     { width: '24%', textAlign },
    colTo:       { width: '44%', textAlign },
    colPrice:    { width: '24%', textAlign: oppAlign },
    routeCity:   { fontSize: 9.5, color: BLACK, lineHeight: 1.45, textAlign },

    totalsBox:   { marginHorizontal: 24, marginTop: 10, alignSelf: isAr ? 'flex-start' : 'flex-end', width: 260 },
    totalsRow:   { flexDirection: rowDir, justifyContent: 'space-between', paddingVertical: 4, paddingHorizontal: 10, borderBottomWidth: 0.5, borderBottomColor: MGRAY },
    totalsLabel: { fontSize: 9.5, color: BLACK },
    totalsVal:   { fontSize: 9.5, color: BLACK },
    grandRow:    { flexDirection: rowDir, justifyContent: 'space-between', paddingVertical: 7, paddingHorizontal: 10, backgroundColor: NAVY, marginTop: 2 },
    grandLabel:  { fontSize: 11, color: WHITE },
    grandVal:    { fontSize: 12, color: WHITE },

    notesBox:    { marginHorizontal: 24, marginTop: 12, padding: 10, backgroundColor: LGRAY, borderLeftWidth: isAr ? 0 : 3, borderRightWidth: isAr ? 3 : 0, borderLeftColor: ACCENT, borderRightColor: ACCENT, borderRadius: 2 },
    notesTitle:  { fontSize: 9.5, color: NAVY, marginBottom: 4, textAlign },
    notesText:   { fontSize: 9, color: BLACK, lineHeight: 1.5, textAlign },

    termsBox:    { marginHorizontal: 24, marginTop: 14 },
    termItem:    { flexDirection: rowDir, marginBottom: 4 },
    termBullet:  { fontSize: 9, color: ACCENT, marginRight: isAr ? 0 : 6, marginLeft: isAr ? 6 : 0 },
    termText:    { flex: 1, fontSize: 8.8, color: BLACK, lineHeight: 1.45, textAlign },

    footer:      { position: 'absolute', bottom: 0, left: 0, right: 0, borderTopWidth: 1, borderTopColor: MGRAY, paddingHorizontal: 24, paddingVertical: 8, flexDirection: rowDir, justifyContent: 'space-between' },
    footerTxt:   { fontSize: 8, color: DGRAY },
  });

  const labels = {
    title:        L('QUOTATION', 'عرض سعر'),
    quoteNo:      L('Quotation No', 'رقم العرض'),
    issued:       L('Issue Date', 'تاريخ الإصدار'),
    validUntil:   L('Valid Until', 'صالح حتى'),
    from:         L('Quotation From', 'من'),
    to:           L('Quotation To', 'إلى'),
    routes:       L('Routes & Pricing', 'المسارات والتسعير'),
    no:           L('#', '#'),
    fromCity:     L('From', 'من'),
    toCity:       L('To', 'إلى'),
    routeCol:     L('Route', 'المسار'),
    price:        L('Price (SAR)', 'السعر (ريال)'),
    subtotal:     L('Subtotal', 'الإجمالي الفرعي'),
    vat:          L('VAT (15%)', 'ضريبة القيمة المضافة (15%)'),
    total:        L('TOTAL', 'الإجمالي'),
    notes:        L('Notes', 'ملاحظات'),
    terms:        L('Terms & Conditions', 'الشروط والأحكام'),
    phone:        L('Phone', 'الهاتف'),
    email:        L('Email', 'البريد الإلكتروني'),
    website:      L('Website', 'الموقع الإلكتروني'),
    vatNo:        L('VAT No', 'الرقم الضريبي'),
    page:         L('Page', 'صفحة'),
    of:           L('of', 'من'),
    thanks:       L('Thank you for your business', 'شكراً لتعاملكم معنا'),
  };

  // Issuer (company) — pulled from settings first, falling back to company doc
  const companyName  = (
    isAr
      ? companySettings?.companyName || companySettings?.companyNameEn
      : companySettings?.companyNameEn || companySettings?.companyName
  ) || company?.companyName || company?.name || L('Company', 'الشركة');
  const companyAddr  = companySettings?.address || company?.address || '';
  const companyPhone = companySettings?.phone || company?.phone || '';
  const companyEmail = companySettings?.email || company?.email || '';
  const companyWebsite = companySettings?.website || company?.website || '';
  const companyVAT   = companySettings?.vatId || companySettings?.vatNumber || company?.vatId || '';
  const logoSrc      = companySettings?.logoBase64 || companySettings?.logoUrl || company?.logoBase64 || company?.logoUrl || null;
  const contactLine = [companyPhone, companyEmail, companyWebsite]
    .map(value => safePdfText(value, ''))
    .filter(Boolean)
    .join(isAr ? ' - ' : '  |  ');
  const registrationLine = [companyAddr, companyVAT]
    .map(value => safePdfText(value, ''))
    .filter(Boolean)
    .join(isAr ? ' - ' : '  |  ');

  const docDate = new Date().toLocaleDateString('en-GB');
  const validUntil = quotation.validUntil
    ? new Date(quotation.validUntil.toDate ? quotation.validUntil.toDate() : quotation.validUntil).toLocaleDateString('en-GB')
    : '-';

  const safeCompanyName = safePdfText(companyName, L('Company', 'الشركة'));
  // Company is the primary identifier; customer/contact name is secondary.
  const customerPrimary = safePdfText(quotation.customerCompany || quotation.customerName || '-');
  const customerSecondary = quotation.customerCompany && quotation.customerName ? safePdfText(quotation.customerName, '') : '';

  const showTotals = !!quotation.combinePricing;

  // Subtotal / VAT / total only matter when showTotals is true
  const routesSubtotal = routes.reduce((sum, r) => sum + (Number(r.price) || 0), 0);
  const subtotal = Number(quotation.totalAmount) || routesSubtotal;
  const vatAmt = subtotal * 0.15;
  const grand = subtotal + vatAmt;

  const cityLabel = (city) => {
    if (!city) return '-';
    if (typeof city === 'object') {
      return safePdfText(isAr ? city.label?.ar || city.name?.ar : city.label?.en || city.name?.en, city.value || '-');
    }
    const c = (options.cities || []).find(x => x.value === city);
    if (!c) return safePdfText(city);
    return safePdfText(isAr ? c.label.ar : c.label.en, city);
  };

  const getRouteSequence = (route) => {
    const raw = (Array.isArray(route.cities) && route.cities.length > 0)
      ? route.cities
      : [route.fromCity, route.toCity];

    return raw
      .filter(Boolean)
      .filter((city, index, list) => index === 0 || city !== list[index - 1]);
  };

  const DestinationPath = ({ route }) => {
    const seq = getRouteSequence(route).slice(1).map(cityLabel);
    if (seq.length === 0) return <Text style={S.routeCity}>-</Text>;
    const separator = isAr ? ' - ' : ' -> ';

    return <Text style={S.routeCity}>{safePdfText(seq.join(separator))}</Text>;
  };

  const termsList = (termsAndConditions[language] || []).filter(t => t && t.trim());

  // Build header sections so RTL flips them visually
  const HeaderCompany = (
    <View style={{ flexDirection: rowDir, flex: 1, alignItems: 'flex-start' }}>
      {logoSrc ? (
        <Image src={logoSrc} style={S.logoImg} />
      ) : (
        <View style={S.logoPlaceholder}>
          <Text style={{ fontSize: 8, color: DGRAY }}>LOGO</Text>
        </View>
      )}
      <View style={S.companyBlock}>
        <Text style={S.companyName}>{safeCompanyName}</Text>
        {contactLine && <Text style={S.companyMetaLine}>{contactLine}</Text>}
        {registrationLine && <Text style={S.companyMetaLine}>{registrationLine}</Text>}
      </View>
    </View>
  );

  const HeaderQuoteBox = (
    <View style={S.quoteBox}>
      <Text style={S.quoteTitle}>{safePdfText(labels.title)}</Text>
      <Text style={S.quoteMetaBold}>{safePdfText(quotation.quotationNumber)}</Text>
      <Text style={S.quoteMetaLine}>{infoLine(labels.issued, docDate)}</Text>
      <Text style={S.quoteMetaLine}>{infoLine(labels.validUntil, validUntil)}</Text>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.topBar} />
        <View style={S.topAccent} />

        {/* Header — flexDirection flips for RTL */}
        <View style={S.headerRow}>
          {HeaderCompany}
          {HeaderQuoteBox}
        </View>

        <View style={S.divider} />

        {/* Parties */}
        <View style={S.partyRow}>
          <View style={[S.partyBox, S.partyBoxL]}>
            <View style={S.partyHead}><Text style={S.partyHeadTxt}>{safePdfText(labels.from)}</Text></View>
            <View style={S.partyBody}>
              <Text style={S.partyName}>{safeCompanyName}</Text>
              {contactLine && <Text style={S.partySub}>{contactLine}</Text>}
              {companyAddr && <Text style={S.partySub}>{safePdfText(companyAddr)}</Text>}
            </View>
          </View>
          <View style={[S.partyBox, S.partyBoxR]}>
            <View style={S.partyHead}><Text style={S.partyHeadTxt}>{safePdfText(labels.to)}</Text></View>
            <View style={S.partyBody}>
              <Text style={S.partyName}>{safePdfText(customerPrimary)}</Text>
              {customerSecondary && <Text style={S.partySub}>{safePdfText(customerSecondary)}</Text>}
              {quotation.customerPhone && <Text style={S.partySub}>{infoLine(labels.phone, quotation.customerPhone)}</Text>}
              {quotation.customerEmail && <Text style={S.partySub}>{infoLine(labels.email, quotation.customerEmail)}</Text>}
            </View>
          </View>
        </View>

        {/* Routes */}
        {routes.length > 0 && (
          <>
            <View style={S.secTitleRow}>
              <View style={S.secBar} />
              <Text style={S.secTitle}>{safePdfText(labels.routes)}</Text>
            </View>
            <View style={S.table}>
              <View style={S.tableHead}>
                <Text style={[S.th, S.colNum]}>{safePdfText(labels.no)}</Text>
                <Text style={[S.th, S.colFrom]}>{safePdfText(labels.fromCity)}</Text>
                <Text style={[S.th, S.colTo]}>{safePdfText(labels.toCity)}</Text>
                <Text style={[S.th, S.colPrice]}>{safePdfText(labels.price)}</Text>
              </View>
              {routes.map((r, i) => {
                const seq = getRouteSequence(r);
                return (
                  <View key={r.id || i} style={[S.tr, i % 2 === 1 ? S.trAlt : null]}>
                    <Text style={[S.td, S.colNum]}>{safePdfText(i + 1)}</Text>
                    <Text style={[S.td, S.colFrom]}>{cityLabel(seq[0] || r.fromCity)}</Text>
                    <View style={[S.td, S.colTo]}>
                      <DestinationPath route={r} />
                    </View>
                    <Text style={[S.td, S.colPrice]}>
                      {showTotals ? '-' : moneyText(r.price)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* Totals — ONLY shown when combinePricing is enabled */}
        {showTotals && (
          <View style={S.totalsBox}>
            <View style={S.totalsRow}>
              <Text style={S.totalsLabel}>{safePdfText(labels.subtotal)}</Text>
              <Text style={S.totalsVal}>{moneyText(subtotal)}</Text>
            </View>
            <View style={S.totalsRow}>
              <Text style={S.totalsLabel}>{safePdfText(labels.vat)}</Text>
              <Text style={S.totalsVal}>{moneyText(vatAmt)}</Text>
            </View>
            <View style={S.grandRow}>
              <Text style={S.grandLabel}>{safePdfText(labels.total)}</Text>
              <Text style={S.grandVal}>{moneyText(grand)}</Text>
            </View>
          </View>
        )}

        {/* Notes */}
        {quotation.description && quotation.description.trim() && (
          <View style={S.notesBox}>
            <Text style={S.notesTitle}>{safePdfText(labels.notes)}</Text>
            {quotation.description.split('\n').map((line, i) => (
              <Text key={i} style={S.notesText}>{safePdfText(line, ' ')}</Text>
            ))}
          </View>
        )}

        {/* Terms & Conditions */}
        {termsList.length > 0 && (
          <View style={S.termsBox}>
            <View style={S.secTitleRow}>
              <View style={S.secBar} />
              <Text style={S.secTitle}>{safePdfText(labels.terms)}</Text>
            </View>
            {termsList.map((term, i) => (
              <View key={i} style={S.termItem}>
                <Text style={S.termBullet}>-</Text>
                <Text style={S.termText}>{safePdfText(term)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Footer */}
        <View style={S.footer} fixed>
          <Text style={S.footerTxt}>{safePdfText(labels.thanks)}</Text>
          <Text
            style={S.footerTxt}
            render={({ pageNumber, totalPages }) => (
              isAr ? safePdfText(`${pageNumber} / ${totalPages}`) : safePdfText(`${labels.page} ${pageNumber} ${labels.of} ${totalPages}`)
            )}
          />
        </View>
      </Page>
    </Document>
  );
};

export default QuotationDocument;
